/*
 * The repair form's three groups.
 *
 * SupportCandy prints its ticket form as one flat list of fields, loaded by
 * AJAX after the page itself. This moves the fields into three fieldsets with
 * legends — About you, Your home, The problem — so the form reads as three
 * short steps and a screen reader announces each group by name.
 *
 * Real nodes are moved rather than reordered with CSS order, so the Tab order
 * matches what is on screen. SupportCandy reads the whole form with FormData
 * and finds fields by class, so moving a field inside the form changes nothing
 * about what is submitted. A field this list does not name is left where the
 * plugin put it, after the groups, so one added later in the SupportCandy
 * admin still renders and submits. If this script never runs, the form is
 * simply ungrouped.
 *
 * The slugs were read from the production form on 23 September 2026.
 */
(function () {
	'use strict';

	var GROUPS = [
		{ title: 'About you', slugs: [ 'name', 'cust_28', 'email' ] },
		{ title: 'Your home', slugs: [ 'cust_29', 'cust_30', 'cust_31', 'cust_32', 'cust_27', 'cust_26' ] },
		{ title: 'The problem', slugs: [ 'category', 'cust_33', 'subject', 'description' ] }
	];

	function buildGroup(form, spec, index) {
		var fieldset = document.createElement('fieldset');
		fieldset.className = 'pastel-repair-group';

		var legend = document.createElement('legend');
		legend.className = 'pastel-repair-legend';

		var step = document.createElement('span');
		step.className = 'pastel-repair-step';
		step.setAttribute('aria-hidden', 'true');
		step.textContent = String(index + 1);

		legend.appendChild(step);
		legend.appendChild(document.createTextNode(spec.title));
		fieldset.appendChild(legend);

		var moved = 0;
		spec.slugs.forEach(function (slug) {
			var field = form.querySelector('div.wpsc-tff.' + slug);
			if (!field) {
				return;
			}
			if (!field.classList.contains('required')) {
				field.classList.add('pastel-repair-optional');
			}
			fieldset.appendChild(field);
			moved += 1;
		});

		return moved ? fieldset : null;
	}

	function group(panel) {
		var form = panel.querySelector('form.wpsc-create-ticket');
		if (!form || form.hasAttribute('data-bayt-grouped')) {
			return;
		}
		// Marked first, so the observer callbacks this function's own moves
		// trigger return straight away.
		form.setAttribute('data-bayt-grouped', '');

		var built = GROUPS.map(function (spec, index) {
			return buildGroup(form, spec, index);
		});

		// Insert the last group first, each at the top, so they land in order.
		// Every field has moved by now, so form.firstChild is a current child.
		for (var i = built.length - 1; i >= 0; i -= 1) {
			if (built[i]) {
				form.insertBefore(built[i], form.firstChild);
			}
		}
	}

	// Wait for the shortened layout to settle before moving the viewport.
	function focusResult(target, panel) {
		var frame = window.requestAnimationFrame || function (fn) { fn(); };
		frame(function () {
			if (target.isConnected === false) { return; }
			if (typeof target.focus === 'function') { target.focus({ preventScroll: true }); }
			var anchor = typeof target.scrollIntoView === 'function' ? target : panel;
			if (typeof anchor.scrollIntoView === 'function') {
				anchor.scrollIntoView({ block: 'start', behavior: 'instant' });
			}
		});
	}

	function reveal(panel) {
		var done = panel.querySelector('.pastel-repair-done');
		if (!done || done.hasAttribute('data-bayt-revealed')) { return; }
		done.setAttribute('data-bayt-revealed', '');
		focusResult(done, panel);
	}

	function guardUploadProgress() {
		var jq = window.jQuery;
		if (!jq) { return; }
		window.wpscAttachmentUploadProgress = function (e) {
			if (!e || !e.lengthComputable || !e.total || !e.currentTarget || !e.currentTarget.attachment) { return; }
			e.currentTarget.attachment.find('.attachment-waiting').each(function () {
				var ring = jq(this);
				if (ring.data('circle-progress')) { ring.circleProgress('value', e.loaded / e.total); }
			});
		};
	}

	var RETRY_DELAYS = [ 4000, 8000, 16000 ];
	var retained = null;
	var submissions = new WeakMap();
	var preparing = new Map();
	var submissionId = 0;
	var SUBMISSION_FIELD = '_bayt_submission_guard';

	function notice(panel, message, failed) {
		var element = panel.querySelector('.pastel-repair-status');
		if (!element) {
			element = document.createElement('div');
			element.className = 'pastel-repair-status ph-no-capture';
			element.setAttribute('data-ph-mask', '');
			element.setAttribute('data-clarity-mask', 'True');
			element.setAttribute('tabindex', '-1');
			panel.insertBefore(element, panel.firstChild);
		}
		element.setAttribute('role', failed ? 'alert' : 'status');
		element.textContent = message;
		focusResult(element, panel);
	}

	function restoreDraft(state) {
		clearTimeout(state.prepareTimer);
		state.cancelled = true;
		if (state.output.parentNode) { state.output.parentNode.removeChild(state.output); }
		state.body.classList.add('wpsc-body');
		state.body.classList.remove('pastel-repair-draft-body');
		state.body.hidden = false;
		retained = null;
	}

	function preparationFailed(state, panel) {
		if (retained !== state || state.started) { return; }
		restoreDraft(state);
		notice(panel, 'Security verification did not finish. Your report and photos are kept in this form. Please try sending it again.', true);
	}

	function refreshCaptcha(body) {
		// A server error can happen after the provider consumed its single-use
		// token. Reset the sole widget on this page for the next manual attempt.
		// Maintenance rejects before WordPress loads, so its tokens stay valid.
		try {
			if (body.querySelector('.cf-turnstile') && document.querySelectorAll('.cf-turnstile').length === 1 &&
				window.turnstile && typeof window.turnstile.reset === 'function') {
				window.turnstile.reset();
			}
			if (body.querySelector('.g-recaptcha') && document.querySelectorAll('.g-recaptcha').length === 1 &&
				window.grecaptcha && typeof window.grecaptcha.reset === 'function') {
				window.grecaptcha.reset();
			}
		} catch (error) { /* Provider availability must not prevent draft recovery. */ }
	}

	// SupportCandy does not handle a rejected reCAPTCHA token promise. Wrap
	// only the ready callback registered by this submit, restoring the provider
	// methods immediately afterwards so other widgets keep their own behaviour.
	function guardCaptcha(state, panel) {
		var captcha = window.grecaptcha;
		if (!captcha || typeof captcha.ready !== 'function' || typeof captcha.execute !== 'function') {
			return function () {};
		}
		var ready = captcha.ready;
		captcha.ready = function (callback) {
			return ready.call(this, function () {
				if (state.cancelled) { return; }
				var execute = captcha.execute;
				captcha.execute = function () {
					return execute.apply(this, arguments).then(undefined, function () {
						preparationFailed(state, panel);
						// The cancelled payload is blocked by the AJAX prefilter.
						return '';
					});
				};
				try { callback(); } catch (error) { preparationFailed(state, panel); }
				finally { captcha.execute = execute; }
			});
		};
		return function () { captcha.ready = ready; };
	}

	function reportOutcome(xhr, textStatus, attempts, maintenance, recovered, started, requestId) {
		try {
			if (typeof window.baytPostHogDiagnostic === 'function') {
				window.baytPostHogDiagnostic('repair_submission', {
					status: xhr ? xhr.status : 0, outcome: recovered ? 'success' : textStatus, attempt: attempts,
					duration_ms: started ? Date.now() - started : 0, request_id: requestId || ''
				});
			}
		} catch (ignored) { /* Recovery never depends on logging. */ }
		var ph = window.posthog;
		if (!ph || !ph.__loaded) { return; }
		var properties = { status: xhr ? xhr.status : 0, text_status: textStatus, attempts: attempts, maintenance: maintenance };
		// Telemetry must never prevent recovery, and contains no report or tenant data.
		try {
			if (recovered && attempts && typeof ph.capture === 'function') {
				ph.capture('repair_submission_recovered', properties);
			} else if (!recovered && typeof ph.captureException === 'function') {
				ph.captureException(new Error('Repair ticket submission could not be confirmed'), properties);
			}
		} catch (error) { /* The report remains recoverable if telemetry is unavailable. */ }
	}

	/*
	 * SupportCandy destroys every .wpsc-body on submit, before starting AJAX.
	 * Keep the actual form/editor/attachment nodes in their original parent:
	 * rename that body and give the plugin a sibling for its loader/result.
	 * No cloning, serialization or browser storage of tenant information.
	 */
	function guardSubmission(panel) {
		var pluginSubmit = window.wpsc_submit_ticket_form;
		if (typeof pluginSubmit !== 'function' || pluginSubmit.baytGuard) { return; }
		var guarded = function () {
			if (retained) { return; }
			var form = panel.querySelector('form.wpsc-create-ticket');
			var body = form && form.closest('.wpsc-body');
			if (!body) { return pluginSubmit.apply(this, arguments); }
			if (body.querySelector('.upload-waiting, .upload-error')) {
				notice(panel, 'Wait for your photos to finish uploading. If a photo failed, remove it and attach it again before sending your report.', true);
				return;
			}
			var turnstile = form.querySelector('[name="cf-turnstile-response"]');
			if (turnstile && !turnstile.value) {
				notice(panel, 'Complete the security verification before sending your report. Your report and photos are kept in this form.', true);
				return;
			}
			var previous = panel.querySelector('.pastel-repair-status');
			if (previous) { previous.parentNode.removeChild(previous); }
			var output = document.createElement('div');
			output.className = body.className;
			body.classList.remove('wpsc-body');
			body.classList.add('pastel-repair-draft-body');
			body.parentNode.insertBefore(output, body.nextSibling);
			var state = { body: body, output: output, started: false };
			retained = state;
			// FormData(form) emits this event before the CAPTCHA callback. Associate
			// its payload with this attempt so a late token cannot send a
			// cancelled attempt, even after the tenant starts another submission.
			// The event's FormData object can differ from the constructor's return.
			// An internal counter crosses that boundary and is removed before AJAX.
			var capture = function (event) {
				var id = String(++submissionId);
				preparing.set(id, state);
				event.formData.set(SUBMISSION_FIELD, id);
			};
			form.addEventListener('formdata', capture);
			var releaseCaptcha = guardCaptcha(state, panel);
			try { pluginSubmit.apply(this, arguments); } catch (error) {
				restoreDraft(state);
				throw error;
			} finally {
				form.removeEventListener('formdata', capture);
				releaseCaptcha();
			}
			// Validation or a pending visibility check has not submitted anything.
			if (retained === state && !state.started && !output.childNodes.length) {
				restoreDraft(state);
			} else if (retained === state) {
				body.hidden = true;
				if (!state.started) {
					notice(panel, 'Preparing to send your repair report…', false);
					state.prepareTimer = setTimeout(function () {
						preparationFailed(state, panel);
					}, 30000);
				}
			}
		};
		guarded.baytGuard = true;
		window.wpsc_submit_ticket_form = guarded;
	}

	function isTicketPost(options) {
		if (options.type !== 'POST' || !(options.data instanceof FormData) ||
			options.data.get('action') !== 'wpsc_set_ticket_form' || typeof options.error !== 'function' ||
			!window.supportcandy || !window.supportcandy.ajax_url) { return false; }
		var target = new URL(options.url, document.baseURI);
		var expected = new URL(window.supportcandy.ajax_url, document.baseURI);
		return target.href === expected.href && target.origin === window.location.origin;
	}

	function maintenanceRejection(xhr) {
		var body = xhr && xhr.responseJSON;
		return !!(xhr && xhr.status === 503 && body && body.code === 'bayt_maintenance' &&
			body.processed === false && typeof xhr.getResponseHeader === 'function' &&
			xhr.getResponseHeader('X-Bayt-Repair-Maintenance') === 'unprocessed');
	}

	function retryDelay(xhr, attempt) {
		var value = xhr.getResponseHeader('Retry-After');
		var seconds = value && /^\d+$/.test(value) ? Number(value) :
			(value ? Math.max(0, (Date.parse(value) - Date.now()) / 1000) : 0);
		// A long Retry-After returns the intact form instead of silently waiting minutes.
		if (!isFinite(seconds) || seconds > 60) { return null; }
		return Math.max(RETRY_DELAYS[attempt], seconds * 1000);
	}

	function guardTicketPost(panel) {
		var jq = window.jQuery;
		if (!jq || typeof jq.ajaxPrefilter !== 'function') { return; }
		jq.ajaxPrefilter(function (options, original, xhr) {
			if (!isTicketPost(options)) { return; }
			var id = options.data.get(SUBMISSION_FIELD);
			var owner = submissions.get(options.data) || preparing.get(id);
			if (id) {
				options.data.delete(SUBMISSION_FIELD);
				preparing.delete(id);
			}
			if (owner) { submissions.set(options.data, owner); }
			if (owner && (owner.cancelled || retained !== owner)) {
				xhr.abort();
				return;
			}
			if (!retained) { return; }
			var state = retained;
			clearTimeout(state.prepareTimer);
			state.started = true;
			var started = Date.now();
			var requestId = requestCorrelation(xhr);
			state.body.hidden = true;
			options.timeout = options.timeout || 30000;
			var pluginError = options.error;
			var pluginSuccess = options.success;
			var attempt = options.baytAttempt || 0;
			notice(panel, 'Sending your repair report…', false);
			options.error = function (xhr, textStatus) {
				if (retained !== state) { return; }
				var maintenance = maintenanceRejection(xhr);
				var delay = maintenance && attempt < RETRY_DELAYS.length ? retryDelay(xhr, attempt) : null;
				if (delay !== null) {
					notice(panel, 'The website is being updated. Your report and photos are kept here. We will try sending it again shortly.', false);
					var retry = jq.extend({}, options, { error: pluginError, success: pluginSuccess, baytAttempt: attempt + 1 });
					setTimeout(function () { if (retained === state) { jq.ajax(retry); } }, delay);
					return;
				}
				restoreDraft(state);
				if (!maintenance) { refreshCaptcha(state.body); }
				var message = maintenance ?
					'The website is still being updated. Your report and photos are kept in this form. Please try sending it again in a moment.' :
					'We could not confirm your report reached us. Your report and photos are kept in this form. Check your confirmation email or your ticket before sending it again.';
				var body = xhr && xhr.responseJSON;
				if (!maintenance && body && body.data && body.data[0] && typeof body.data[0].message === 'string') {
					message = body.data[0].message + ' Your report and photos are kept in this form.';
				}
				notice(panel, message, true);
				reportOutcome(xhr, textStatus, attempt, maintenance, false, started, requestId);
				// Do not run the plugin's error callback: it clears the editor and resets the form.
			};
			options.success = function (response, textStatus, xhr) {
				if (retained !== state) { return; }
				var valid = response && (
					(response.action === 'text' && typeof response.thankyouText === 'string' && response.thankyouText.trim()) ||
					(response.action === 'ticket' && Number(response.id) > 0) ||
					(response.action === 'url' && typeof response.redirectURL === 'string' && response.redirectURL)
				);
				if (!valid) { options.error(xhr, 'parsererror'); return; }
				pluginSuccess.apply(this, arguments);
				jq(state.body).remove();
				retained = null;
				var status = panel.querySelector('.pastel-repair-status');
				if (status) { status.parentNode.removeChild(status); }
				reveal(panel);
				if (response.action === 'text' && !panel.querySelector('.pastel-repair-done')) {
					var fallback = state.output.firstElementChild;
					if (fallback) {
						fallback.setAttribute('role', 'status');
						fallback.setAttribute('tabindex', '-1');
						focusResult(fallback, panel);
					}
				}
				reportOutcome(xhr, textStatus, attempt, !!attempt, true, started, requestId);
			};
		});
	}

	function requestCorrelation(xhr) {
		var id = window.crypto && typeof window.crypto.randomUUID === 'function' ?
			window.crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
				var r = Math.floor(Math.random() * 16); return (c === 'x' ? r : (r & 3 | 8)).toString(16);
			});
		if (typeof xhr.setRequestHeader !== 'function') { return id; }
		xhr.setRequestHeader('X-Bayt-Request-ID', id);
		try {
			if (window.baytConsent && window.baytConsent.allows('analytics') && window.posthog &&
				typeof window.posthog.get_session_id === 'function') {
				var session = window.posthog.get_session_id();
				if (typeof session === 'string' && /^[0-9a-f-]{36}$/i.test(session)) { xhr.setRequestHeader('X-Bayt-Session-ID', session); }
			}
		} catch (ignored) { /* Analytics availability never blocks the form. */ }
		return id;
	}

	function guardFormLoad(panel) {
		var jq = window.jQuery;
		if (!jq || panel.hasAttribute('data-bayt-load-guard')) { return; }
		panel.setAttribute('data-bayt-load-guard', '');
		jq.ajaxPrefilter(function (options, original, xhr) {
			if (!window.supportcandy || !window.supportcandy.ajax_url || String(options.type).toUpperCase() !== 'POST') { return; }
			var target = new URL(options.url, document.baseURI);
			var expected = new URL(window.supportcandy.ajax_url, document.baseURI);
			var data = typeof options.data === 'string' ? new URLSearchParams(options.data) : null;
			if (target.origin !== window.location.origin || target.href !== expected.href ||
				!data || data.get('action') !== 'wpsc_get_ticket_form') { return; }
			var start = performance.now();
			options.timeout = 20000;
			var success = options.success;
			var error = options.error;
			var requestId = requestCorrelation(xhr);
			function report(outcome, response) {
				try {
					if (typeof window.baytPostHogDiagnostic === 'function') {
						window.baytPostHogDiagnostic('repair_form_load', { outcome: outcome, status: response.status || 0,
							duration_ms: Math.round(performance.now() - start), page_elapsed_ms: Math.round(performance.now()), request_id: requestId });
					}
				} catch (ignored) { /* Logging is secondary to loading the form. */ }
			}
			function failed(response, status) {
				report(status || 'error', response);
				if (typeof error === 'function') { error.apply(this, arguments); }
				// This request only reads a blank form. Never replace a tenant's draft.
				if (panel.querySelector('form.wpsc-create-ticket')) { return; }
				var body = panel.querySelector('.wpsc-body');
				if (body) { body.textContent = ''; }
				var container = panel.querySelector('#wpsc-container');
				if (container) { container.style.display = 'block'; }
				notice(panel, 'The repair form could not load. Try loading it again, or reload this page. For an urgent issue, call us.', true);
				var button = document.createElement('button');
				button.type = 'button';
				button.className = 'button button-ink';
				button.textContent = 'Try loading the form again';
				button.addEventListener('click', function () {
					var status = panel.querySelector('.pastel-repair-status');
					if (status) { status.remove(); }
					if (typeof window.wpsc_get_ticket_form === 'function') { window.wpsc_get_ticket_form(); }
				});
				panel.querySelector('.pastel-repair-status').appendChild(button);
			}
			options.error = failed;
			options.success = function (html, status, response) {
				if (typeof html !== 'string' || !/<form\b[^>]*\bclass=["'][^"']*\bwpsc-create-ticket\b/i.test(html)) {
					failed(response, 'invalid_response'); return;
				}
				if (typeof success === 'function') { success.apply(this, arguments); }
				var previous = panel.querySelector('.pastel-repair-status');
				if (previous) { previous.remove(); }
				report('success', response);
			};
		});
	}

	// The deferred script executes before DOMContentLoaded. Register now:
	// SupportCandy's earlier jQuery-ready handler starts its AJAX immediately.
	var initialPanel = document.querySelector('div.pastel-ticket-panel[data-repair-view="create"]');
	if (initialPanel) { guardFormLoad(initialPanel); }

	function init() {
		guardUploadProgress();
		var panel = document.querySelector('div.pastel-ticket-panel[data-repair-view="create"]');
		if (!panel) { return; }
		guardFormLoad(panel);
		guardTicketPost(panel);
		group(panel);
		guardSubmission(panel);
		reveal(panel);
		if (typeof MutationObserver === 'function') {
			new MutationObserver(function () {
				group(panel);
				guardSubmission(panel);
				reveal(panel);
			}).observe(panel, { childList: true, subtree: true });
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
